import { useQuery } from "@tanstack/react-query";
import { getEventGuestQuota } from "@/lib/api/clients/EventClient";
import { qk } from "@/lib/query/queryKeys";
import type { EventGuestQuota } from "@/types/EventGuestQuota";

/**
 * Guest contingent for one event, read from the company that OWNS it.
 *
 * Replaces `getCompany("contingent,blocked")` everywhere an event is in scope.
 * That call resolves the company from the login cookie, so on an event
 * belonging to another company it returned a balance that governs nothing —
 * while the create-guest / import / CRM-transfer gates were reading it.
 *
 * Goes through the CLIENT, not the handler, so a failure throws and the query
 * lands in `isError` instead of resolving to a ServerError object the callers
 * would have to sniff. Same choice `useEvent` makes.
 */
export function useEventGuestQuota(eventId: string | null) {
  return useQuery<EventGuestQuota>({
    queryKey: eventId ? qk.events.guestQuota(eventId) : ["events", "guest-quota", "none"],
    queryFn: () => getEventGuestQuota(eventId!),
    enabled: !!eventId,
    staleTime: 30_000,
  });
}
